import React, { useRef, useState } from 'react';
import { Plus, ArrowDown, Save, X, ImagePlus, Trash2 } from 'lucide-react';
import {
    DndContext,
    closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortablePart from './SortablePart';
import SortableStep from './SortableStep';
import CategoryDropdown from './CategoryDropdown';
import ImageCropModal from './ImageCropModal';
import './RecipeForm.css';

const RecipeForm = ({
    formData,
    onInputChange,
    onPartChange,
    onStepChange,
    onIngredientChange,
    onAddPart,
    onRemovePart,
    onAddStep,
    onRemoveStep,
    onRemoveIngredient,
    onStepImageUpload,
    onStepImageRemove,
    onCoverImageUpload,
    onCoverImageRemove,
    categories,
    onCategoryChange,
    sensors,
    onDragEnd,
    onSubmit,
    onCancel,
    loading,
    submitLabel = '保存食谱',
    loadingLabel = '保存中...',
}) => {
    return (
        <form onSubmit={onSubmit} className="recipe-form">
            <div className="form-section">
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="title">食谱标题 *</label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={onInputChange}
                            className="form-control"
                            placeholder="输入食谱标题"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="servings">份量</label>
                        <div className="serving-input-group">
                            <input
                                type="number"
                                id="servings"
                                name="servings"
                                value={formData.servings}
                                onChange={onInputChange}
                                className="form-control"
                                placeholder="份数"
                                min="1"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => onInputChange({
                                    target: {
                                        name: 'servingUnit',
                                        value: formData.servingUnit === '份' ? 'ml/g' : '份'
                                    }
                                })}
                                className="serving-unit-btn"
                            >
                                {formData.servingUnit || '份'}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="category">分类</label>
                    <CategoryDropdown
                        value={formData.category}
                        onChange={onCategoryChange}
                        categories={categories}
                        placeholder="输入或选择分类"
                    />
                </div>

                <div className="form-group">
                    <label>封面图</label>
                    <CoverImageUpload
                        imageUrl={formData.coverImage}
                        onUpload={onCoverImageUpload}
                        onRemove={onCoverImageRemove}
                    />
                </div>
            </div>

            <div className="form-section">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                >
                    <div className="parts-container">
                        <SortableContext
                            items={formData.parts.map((_, index) => `part-${index}`)}
                            strategy={verticalListSortingStrategy}
                        >
                            {formData.parts.map((part, partIndex) => (
                                <SortablePart
                                    key={partIndex}
                                    part={part}
                                    partIndex={partIndex}
                                    onRemovePart={formData.parts.length > 1 ? onRemovePart : null}
                                    onPartChange={onPartChange}
                                >
                                    <div className="steps-container">
                                        <SortableContext
                                            items={part.steps.map((_, index) => `step-${partIndex}-${index}`)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {part.steps.map((step, stepIndex) => (
                                                <React.Fragment key={stepIndex}>
                                                    <SortableStep
                                                        step={step}
                                                        stepIndex={stepIndex}
                                                        partIndex={partIndex}
                                                        onRemoveStep={part.steps.length > 1 ? onRemoveStep : null}
                                                    >
                                                        <div className="step-content">
                                                            <div className="ingredients-section">
                                                                {step.ingredients.map((ingredient, ingredientIndex) => (
                                                                    <div key={ingredientIndex} className="ingredient-input-group">
                                                                        <input
                                                                            type="text"
                                                                            value={ingredient.name}
                                                                            onChange={(e) => onIngredientChange(partIndex, stepIndex, ingredientIndex, 'name', e.target.value)}
                                                                            className="form-control ingredient-name"
                                                                            placeholder="配料名称"
                                                                        />
                                                                        <input
                                                                            type="text"
                                                                            value={ingredient.weight}
                                                                            onChange={(e) => onIngredientChange(partIndex, stepIndex, ingredientIndex, 'weight', e.target.value)}
                                                                            className="form-control ingredient-weight"
                                                                            placeholder="重量"
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => onRemoveIngredient(partIndex, stepIndex, ingredientIndex)}
                                                                            className="remove-ingredient-btn"
                                                                        >
                                                                            <X size={14} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            <div className="step-details-section">
                                                                <textarea
                                                                    value={step.details}
                                                                    onChange={(e) => onStepChange(partIndex, stepIndex, 'details', e.target.value)}
                                                                    className="form-control step-details"
                                                                    placeholder="描述在此步骤中要做什么..."
                                                                    rows="4"
                                                                />
                                                                <StepImageUpload
                                                                    imageUrl={step.imageUrl}
                                                                    onUpload={(file) => onStepImageUpload(partIndex, stepIndex, file)}
                                                                    onRemove={() => onStepImageRemove(partIndex, stepIndex)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </SortableStep>

                                                    {stepIndex < part.steps.length - 1 && (
                                                        <div className="step-arrow">
                                                            <ArrowDown size={20} />
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            ))}

                                            <button
                                                type="button"
                                                onClick={() => onAddStep(partIndex)}
                                                className="add-step-btn"
                                            >
                                                <Plus size={20} />
                                            </button>
                                        </SortableContext>
                                    </div>
                                </SortablePart>
                            ))}
                        </SortableContext>
                    </div>

                    <button
                        type="button"
                        onClick={onAddPart}
                        className="add-part-btn"
                    >
                        <Plus size={24} />
                        添加新部分
                    </button>
                </DndContext>
            </div>

            <div className="form-actions">
                <button
                    type="button"
                    onClick={onCancel}
                    className="btn btn-secondary"
                    disabled={loading}
                >
                    取消
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                >
                    <Save size={16} />
                    {loading ? loadingLabel : submitLabel}
                </button>
            </div>
        </form>
    );
};

/**
 * Sub-component for step image upload/preview/remove
 */
const StepImageUpload = ({ imageUrl, onUpload, onRemove }) => {
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            onUpload(file);
            e.target.value = '';
        }
    };

    return (
        <div className="step-image-section">
            {imageUrl ? (
                <div className="step-image-preview">
                    <img src={imageUrl} alt="步骤图片" />
                    <button
                        type="button"
                        onClick={onRemove}
                        className="remove-image-btn"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="upload-image-btn"
                >
                    <ImagePlus size={16} />
                    添加图片
                </button>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
        </div>
    );
};

/**
 * Sub-component for cover image upload/preview/remove (square) with crop modal
 */
const CoverImageUpload = ({ imageUrl, onUpload, onRemove }) => {
    const fileInputRef = useRef(null);
    const [cropSrc, setCropSrc] = useState(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => setCropSrc(ev.target.result);
            reader.readAsDataURL(file);
            e.target.value = '';
        }
    };

    const handleCropDone = (croppedDataUrl) => {
        setCropSrc(null);
        onUpload(croppedDataUrl);
    };

    return (
        <div className="cover-image-section">
            {imageUrl ? (
                <div className="cover-image-preview">
                    <img src={imageUrl} alt="封面图" />
                    <button
                        type="button"
                        onClick={onRemove}
                        className="remove-image-btn"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="upload-cover-btn"
                >
                    <ImagePlus size={20} />
                    <span>上传封面图</span>
                </button>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            {cropSrc && (
                <ImageCropModal
                    imageSrc={cropSrc}
                    onCropComplete={handleCropDone}
                    onCancel={() => setCropSrc(null)}
                    aspect={1}
                />
            )}
        </div>
    );
};

export default RecipeForm;
